import React, { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { proofPoints } from "../data/siteContent";
import "../styles/domain-pages.css";

const lifecycleNodes = [
  {
    label: "Client Acquisition",
    title: "Unifying multi-channel acquisition funnels",
    copy: "Capture and consolidate lead data from broker portals, direct website enquiries, referral campaigns and sales teams before prospective customer data fragments across disconnected channels.",
    signals: [
      { label: "Sources", value: "Broker portals, web enquiries, referrals" },
      { label: "Control", value: "Lead source and consent capture" },
      { label: "Outcome", value: "Clean acquisition funnel visibility" },
    ],
  },
  {
    label: "Qualification & Conversion",
    title: "Tracking conversion velocity and customer intent",
    copy: "Monitor eligibility checks, product fit, lead status, customer consent and conversion events so the business can see how an enquiry matures into a formal application.",
    signals: [
      { label: "Sources", value: "Eligibility, CRM status, consent events" },
      { label: "Control", value: "Qualification and handoff checkpoints" },
      { label: "Outcome", value: "Conversion velocity and drop-off insight" },
    ],
  },
  {
    label: "Application & Assessment",
    title: "Aggregating credit assessment intelligence",
    copy: "Centralise customer documentation, income verification, credit checks, risk assessment outputs and manual exceptions into a single auditable data layer.",
    signals: [
      { label: "Sources", value: "Documents, credit checks, assessment outputs" },
      { label: "Control", value: "Exception and verification lineage" },
      { label: "Outcome", value: "Auditable credit assessment view" },
    ],
  },
  {
    label: "Approval & Settlement",
    title: "Eliminating data friction at the point of funding",
    copy: "Track approved files through offer generation, contract execution, settlement instructions, funding and account creation so data does not drop between origination and active portfolios.",
    signals: [
      { label: "Sources", value: "Offers, contracts, settlement instructions" },
      { label: "Control", value: "Funding and account creation checks" },
      { label: "Outcome", value: "Cleaner settlement handover" },
    ],
  },
  {
    label: "Servicing & Payments",
    title: "Securing transaction settlement and ledger event streams",
    copy: "Connect repayment schedules, balances, direct debit files, direct credit files, transaction events and customer servicing activity into operational feedback loops.",
    signals: [
      { label: "Sources", value: "Schedules, balances, direct debit files" },
      { label: "Control", value: "Payment failure and exception capture" },
      { label: "Outcome", value: "Operational servicing feedback loops" },
    ],
  },
  {
    label: "Reconciliation & Audit",
    title: "Securing structural compliance",
    copy: "Cross-validate loan records, ledger changes, payment events and clearing records so finance, risk and compliance teams can trust audit-facing reporting.",
    signals: [
      { label: "Sources", value: "Loan records, ledgers, clearing events" },
      { label: "Control", value: "Multi-system reconciliation checks" },
      { label: "Outcome", value: "Audit-ready lending evidence" },
    ],
  },
];

const solutionMatrix = [
  {
    title: "Compression of Time-to-Settlement",
    copy: "By establishing data continuity across acquisition, origination and ledger systems, we reduce manual data re-entry, repeated validation and handover friction that slows time-to-funding.",
  },
  {
    title: "De-Risked Institutional System Migrations",
    copy: "Move away from legacy ledger engines or fragmented origination tools without losing operational history. We design schema mappings, transformation rules and migration control frameworks.",
  },
  {
    title: "Automated Operational Exception Handling",
    copy: "Move from passive month-end reporting to event-driven platforms that surface clearing failures, payment exceptions and servicing anomalies as they happen.",
  },
];

const medallionLayers = [
  {
    layer: "Bronze",
    copy: "Raw, programmatic metadata-driven ingestion targeting source schemas across core banking platforms, loan origination engines, CRM applications and operational spreadsheets.",
  },
  {
    layer: "Silver",
    copy: "Harmonised, deduplicated and deeply validated datasets matching disparate customer accounts across multi-vendor software networks.",
  },
  {
    layer: "Gold",
    copy: "Curated consumption layers and semantic models optimised for Power BI deployment, cash flow forecasting and automated audit review cycles.",
  },
];

const feedbackLoops = [
  "Direct automated ingestion of clearing processing logs from banking networks.",
  "Immediate extraction of transaction exceptions, payment failures and account rejections.",
  "Event-driven operational triggers that alert backend teams before an issue breaches an agreed service threshold.",
];

const financeIntegrityControls = [
  "Transaction lineage",
  "Payment exception handling",
  "Secure file movement",
  "Reconciliation checks",
  "Migration validation",
  "Audit evidence",
];

export default function FinancialServicesLendingData() {
  const [activeNode, setActiveNode] = useState(lifecycleNodes[0]);

  return (
    <div className="site-page finance-domain-page">
      <Seo
        title="Financial Services & Lending Data Platforms | Data Apparatus"
        description="Financial services data engineering for settlement velocity, lending lifecycle visibility, core banking migrations, PGP encrypted SFTP payments and audit-ready reporting."
      />

      <section className="page-hero finance">
        <div className="page-hero-copy">
          <span className="eyebrow">Financial services and lending data</span>
          <h1>Financial Services & Lending Data Platforms</h1>
          <h2>
            Engineering the data infrastructure that bridges client acquisition,
            credit origination and active portfolio ledger systems, reducing
            operational blind spots across the lending lifecycle.
          </h2>
          <div className="hero-actions">
            <Link to="/contact" className="primary-btn">
              Discuss lending data
            </Link>
            <a href="#lifecycle" className="secondary-btn">
              View lifecycle model
            </a>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="content-split">
          <div>
            <span className="eyebrow">Core focus</span>
            <h2>Most financial institutions have a broken data lineage.</h2>
            <p>
              When data is siloed across acquisition, underwriting and banking software
              networks, the business loses visibility on margins, processing risk and
              capital performance. We remove this friction by modelling clean data
              lineages that match the heartbeat of lending operations.
            </p>
          </div>
          <div className="finance-solution-matrix">
            {solutionMatrix.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lifecycle-section" id="lifecycle">
        <div className="section-heading">
          <span className="eyebrow">Lending operating lifecycle</span>
          <h2>From client acquisition to conversion, settlement, servicing and audit-ready reporting.</h2>
          <p>
            Select a business stage to see how the data foundation supports decision
            visibility, reconciliation and controlled reporting across the lending process.
          </p>
        </div>

        <div className="lifecycle-workflow">
          <div className="lifecycle-nodes">
            {lifecycleNodes.map((node, index) => (
              <button
                type="button"
                className={activeNode.label === node.label ? "active" : ""}
                key={node.label}
                onClick={() => setActiveNode(node)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                {node.label}
              </button>
            ))}
          </div>
          <aside className="lifecycle-detail">
            <span className="eyebrow">Selected ecosystem stage</span>
            <h3>{activeNode.title}</h3>
            <p>{activeNode.copy}</p>
            <div className="lifecycle-stage-signals">
              {activeNode.signals.map((signal) => (
                <div key={signal.label}>
                  <span>{signal.label}</span>
                  <strong>{signal.value}</strong>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="page-section alt">
        <div className="content-split">
          <div>
            <span className="eyebrow">Enterprise cloud standards</span>
            <h2>The financial medallion data architecture model.</h2>
            <p>
              We design automated data platforms across Microsoft Fabric, Azure and
              Snowflake that separate raw banking endpoints into structured layers built
              for integrity, validation and controlled reporting.
            </p>
          </div>
          <div className="medallion-clean">
            {medallionLayers.map((layer) => (
              <div key={layer.layer}>
                <strong>{layer.layer} Layer</strong>
                <p>{layer.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="feature-block-section">
        <div className="feature-block">
          <div className="feature-copy">
            <span className="eyebrow">Transaction execution and controls</span>
            <h2>Data platforms must trigger operational actions, not just reporting views.</h2>
            <p>
              We build secure, event-driven pipelines using serverless architecture and
              reliable cloud triggers to identify transaction processing failures,
              clearing-house anomalies and operational exceptions quickly.
            </p>
            <ul className="feature-list">
              {feedbackLoops.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="audit-trust-card" aria-hidden="true">
            <div className="audit-header">Compliance trust audit monitor</div>
            <div className="audit-metric">
              <strong>100%</strong>
              <span>Data lineage reconciled</span>
            </div>
            <div className="audit-tags">
              <span>PGP Encrypted</span>
              <span>Secure SFTP Pipeline</span>
              <span>Ledger Backup Metric</span>
            </div>
            <div className="audit-status">
              <i></i>
              System ledger checks and multi-system reconciliation matches fully validated
            </div>
          </div>
        </div>
      </section>

      <section className="page-section finance-integrity-section">
        <div className="finance-integrity-inner">
          <div className="section-heading left">
            <span className="eyebrow">Delivery integrity</span>
            <h2>Designed for environments where data corruption, broken transactions and failed audits are unacceptable.</h2>
            <p>
              The delivery approach balances engineering discipline with business strategy.
              We protect core banking transactional history, loan servicing workflows and
              system integration lineages.
            </p>
          </div>

          <div className="finance-integrity-grid">
            {financeIntegrityControls.map((control) => (
              <div key={control}>{control}</div>
            ))}
          </div>

          <div className="finance-evidence-strip">
            <span className="eyebrow">Relevant delivery evidence</span>
            {proofPoints.slice(0, 2).map((point) => (
              <div key={point}>{point}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band">
        <h2>Ready to de-risk an upcoming platform migration or automate manual transaction processing?</h2>
        <p>
          Partner with a data engineering team that builds institutional data discipline
          into your operational tools.
        </p>
        <Link to="/contact" className="primary-btn">
          Book a 30-minute architecture review
        </Link>
      </section>

      <Footer />
    </div>
  );
}
